// Single source of truth for role identifiers. Values are the exact strings
// the backend sends in `user.role`; never rename or merge them.
export const ROLES = Object.freeze({
  NEW: "new", // new client: onboarding only
  CEO: "ceo",
  FA: "fa", // financial audit
  CTO: "cto",
  DEVELOPER: "developer",
  AM: "am", // account manager
  DM: "dm", // data management
  DMM: "dmm", // data management manager
  STORE: "store", // client
  STORE_L: "storeL", // client, L package
  BRANCH: "branch", // branch responsible (the cashier business function)
  MENU_SP: "menuSp", // LUD menu specialist
  FEEDUS: "feedus", // integration partner
  SALES: "sales",
});

export const ROLE_LIST = Object.freeze(Object.values(ROLES));

// The roles an administrator can assign in the Users form, in the order the form lists them. Must match the backend role enum
// (models/user.js); the legacy "cashier" user role is retired (NA-016) and cannot be assigned.
export const ASSIGNABLE_ROLES = Object.freeze([
  ROLES.NEW, ROLES.CEO, ROLES.FA, ROLES.CTO, ROLES.DEVELOPER, ROLES.MENU_SP, ROLES.FEEDUS, ROLES.AM, ROLES.DM, ROLES.DMM,
  ROLES.STORE, ROLES.STORE_L, ROLES.BRANCH, ROLES.SALES,
]);

// ceo / cto / developer share one set of grants today (`ceoAccess` in the old
// Context). Module grants spell this out with `lud(...)` / `CEO_LEVEL`.
export const CEO_LEVEL = Object.freeze([ROLES.CEO, ROLES.CTO, ROLES.DEVELOPER]);

// Roles whose restaurant list is limited by `user.between` when it is set.
// Staff with an empty `between` are not restricted by the frontend (the
// backend filters the data); client roles with an empty list reach nothing.
export const GLOBAL_SCOPE_ROLES = CEO_LEVEL;
// Staff roles whose restaurants are all of them when the user holds `globalAccess: true` (the backend
// utils/access GLOBAL_ELIGIBLE_ROLES). It widens WHICH restaurants, never what the role may do.
export const GLOBAL_ELIGIBLE_ROLES = Object.freeze([
  ROLES.AM,
  ROLES.DM,
  ROLES.DMM,
  ROLES.MENU_SP,
  ROLES.SALES,
  ROLES.FEEDUS,
]);
export const STAFF_ROLES = Object.freeze([
  ROLES.FA,
  ROLES.AM,
  ROLES.DM,
  ROLES.DMM,
  ROLES.MENU_SP,
  ROLES.FEEDUS,
  ROLES.SALES,
]);

// Package roles behave like a base role minus some grants. Used for the
// PACKAGE_RESTRICTED reason code and as the extension point for future tiers.
export const PACKAGE_ROLES = Object.freeze({
  [ROLES.STORE_L]: ROLES.STORE,
});

// A prospective client (`new`) becomes the base role when the subscription of its store activates (backend
// utils/access/clientLifecycle). What the base role holds and the prospect does not is reported as SUBSCRIPTION_REQUIRED
// ("locked until a subscription is active"), not as a plain denial, so the UI can show the action, explain it and link to billing.
export const PROSPECT_ROLES = Object.freeze({
  [ROLES.NEW]: ROLES.STORE,
});

// The roles whose operational actions depend on the store's subscription: the client owners. LUD staff act on behalf of clients and are
// never locked by a client's subscription; a `branch` user works inside the store's own operations (unchanged, see
// lud-backend/docs/architecture.md, Client lifecycle).
export const SUBSCRIPTION_GATED_ROLES = Object.freeze([ROLES.NEW, ROLES.STORE, ROLES.STORE_L]);

export const isKnownRole = (role) => ROLE_LIST.includes(role);

// The actual `user.role` is authoritative (NA-020). Assigned branches are
// resource scope (see scope.js), never a role: a ceo/am/dmm with assigned
// branches keeps their own grants, and a branch user stays `branch`.
// The retired `cashier` user role (NA-016) is not in ROLE_LIST, so it is an
// unknown role and is denied everywhere.
export const roleOf = (user) =>
  user && typeof user.role === "string" ? user.role : null;
